import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';

import { cn } from '../../lib/utils';
import { buttonVariants } from '../../components/ui/button';

function AlertDialog({ ...props }) {
    return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />;
}

function AlertDialogTrigger({ ...props }) {
    return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogPortal({ children, ...props }) {
    return (
        <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props}>
            <div className="tailwind-scope tw-font-theme">{children}</div>
        </AlertDialogPrimitive.Portal>
    );
}

function AlertDialogOverlay({ className, ...props }) {
    return (
        <AlertDialogPrimitive.Overlay
            data-slot="alert-dialog-overlay"
            className={cn(
                'tw-fixed tw-inset-0 tw-z-50 tw-bg-black/50 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0',
                className
            )}
            {...props}
        />
    );
}

function AlertDialogContent({ className, overlayClassName, ...props }) {
    return (
        <AlertDialogPortal>
            <AlertDialogOverlay className={overlayClassName} />
            <AlertDialogPrimitive.Content
                data-slot="alert-dialog-content"
                className={cn(
                    'tw-fixed tw-left-[50%] tw-top-[50%] tw-z-50 tw-grid tw-w-full tw-max-w-[calc(100%-2rem)] tw-translate-x-[-50%] tw-translate-y-[-50%] tw-gap-4 tw-rounded-lg tw-border tw-bg-background tw-p-6 tw-shadow-lg tw-duration-200 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 sm:tw-max-w-lg',
                    className
                )}
                {...props}
            />
        </AlertDialogPortal>
    );
}

function AlertDialogHeader({ className, ...props }) {
    return (
        <div
            data-slot="alert-dialog-header"
            className={cn('tw-flex tw-flex-col tw-gap-2 tw-text-center sm:tw-text-left', className)}
            {...props}
        />
    );
}

function AlertDialogFooter({ className, ...props }) {
    return (
        <div
            data-slot="alert-dialog-footer"
            className={cn(
                'tw-flex tw-flex-col-reverse tw-gap-2 sm:tw-flex-row sm:tw-justify-end',
                className
            )}
            {...props}
        />
    );
}

function AlertDialogTitle({ className, ...props }) {
    return (
        <AlertDialogPrimitive.Title
            data-slot="alert-dialog-title"
            className={cn('tw-text-lg tw-font-semibold', className)}
            {...props}
        />
    );
}

function AlertDialogDescription({ className, ...props }) {
    return (
        <AlertDialogPrimitive.Description
            data-slot="alert-dialog-description"
            className={cn('tw-text-sm tw-text-muted-foreground', className)}
            {...props}
        />
    );
}

function AlertDialogAction({ className, variant = 'default', ...props }) {
    return <AlertDialogPrimitive.Action className={cn(buttonVariants({variant}), className)} {...props} />;
}

function AlertDialogCancel({ className, ...props }) {
    return (
        <AlertDialogPrimitive.Cancel
            className={cn(buttonVariants({ variant: 'outline' }), className)}
            {...props}
        />
    );
}

export {
    AlertDialog,
    AlertDialogPortal,
    AlertDialogOverlay,
    AlertDialogTrigger,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogAction,
    AlertDialogCancel,
};
